/**
 * Hook providing template options from the Template Library for use
 * in Rules Engine action editors. Fetches Questionnaire resources from
 * Medplum, merges local drafts, and falls back to mock catalog in mock mode.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire } from '@medplum/fhirtypes';

import { isMockMedplumMode } from '@/lib/isMockMedplumMode';
import { getTemplateCategory } from '@/pages/settings/template-library/utils/templateTags';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_CATEGORY_SYSTEM,
  type TemplateCategory,
} from '@/pages/settings/template-library/types';
import { getAllLocalTemplates, subscribeToLocalTemplates } from '@/pages/settings/template-library/localTemplateStore';
import { getMockQuestionnairesForIntake } from '@/pages/settings/template-library/mockTemplates';
import type { FilterMultiSelectSection } from '@/components/FilterMultiSelect';
import type { TemplateLibraryRef } from '../types';
import {
  inferTemplateType,
  isAssignableFormTemplate,
  isEmailTemplate,
} from '../utils/templateCategoryClassify';
import { filterClientAssignableTemplates, isClientShareRestrictedTemplate } from '@/lib/clientShareRestrictedTemplates';

export interface TemplateOption {
  value: string;
  label: string;
  description?: string;
  category?: string;
  type: 'email' | 'form' | 'document' | string;
  ref: TemplateLibraryRef;
}

export { inferTemplateType, isAssignableFormTemplate, isEmailTemplate };

export function questionnaireToTemplateOption(q: Questionnaire): TemplateOption {
  const category = getTemplateCategory(q);
  const rawCategory = q.meta?.tag?.find((t) => t.system === TEMPLATE_CATEGORY_SYSTEM)?.code;
  const type = isEmailTemplate(rawCategory) ? 'email' : inferTemplateType(category);
  return {
    value: q.id ?? '',
    label: q.title ?? q.name ?? 'Untitled Template',
    description: q.description,
    category,
    type,
    ref: {
      id: q.id ?? '',
      title: q.title ?? q.name ?? 'Untitled Template',
      category,
      type,
      canonicalUrl: q.url,
      version: q.version,
    },
  };
}

export function loadTemplateLibraryOptions(
  questionnaires: Questionnaire[],
): TemplateOption[] {
  return questionnaires
    .filter((q) => q.id)
    .map(questionnaireToTemplateOption);
}

function mergeTemplateOptions(primary: TemplateOption[], extra: TemplateOption[]): TemplateOption[] {
  const byId = new Map<string, TemplateOption>();
  for (const opt of [...primary, ...extra]) {
    if (opt.value) byId.set(opt.value, opt);
  }
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Build FilterMultiSelect sections grouped by Template Library category tab.
 * When `categories` is provided, only those categories are included, in that order.
 */
export function buildTemplateLibraryFilterSections(
  options: TemplateOption[],
  categories: readonly TemplateCategory[] = TEMPLATE_CATEGORIES,
): FilterMultiSelectSection[] {
  const allowed = new Set<TemplateCategory>(categories);
  const byCategory = new Map<string, { value: string; label: string }[]>();
  for (const opt of options) {
    const cat = opt.category as TemplateCategory | undefined;
    if (!cat || !allowed.has(cat)) continue;
    const groupLabel = TEMPLATE_CATEGORY_LABELS[cat];
    const list = byCategory.get(groupLabel) ?? [];
    list.push({ value: opt.value, label: opt.label });
    byCategory.set(groupLabel, list);
  }

  const sections: FilterMultiSelectSection[] = [];
  for (const cat of categories) {
    const label = TEMPLATE_CATEGORY_LABELS[cat];
    const items = byCategory.get(label);
    if (items?.length) {
      sections.push({ label, data: items });
    }
  }
  return sections;
}

/** Categories shown in Assign Forms action picker, in display order. */
export const FORM_ASSIGNMENT_TEMPLATE_CATEGORIES = [
  'forms',
  'measures',
] as const satisfies readonly TemplateCategory[];

export function buildFormAssignmentTemplateFilterSections(
  options: TemplateOption[],
): FilterMultiSelectSection[] {
  const assignable = filterClientAssignableTemplates(options);
  return buildTemplateLibraryFilterSections(assignable, FORM_ASSIGNMENT_TEMPLATE_CATEGORIES);
}

async function fetchTemplateQuestionnaires(medplum: ReturnType<typeof useMedplum>): Promise<Questionnaire[]> {
  const bundle = await medplum.search('Questionnaire', {
    status: 'active,draft',
    _count: '500',
    _sort: 'title',
  });
  return (bundle.entry ?? [])
    .map((e) => e.resource as Questionnaire)
    .filter(Boolean);
}

function mockTemplateQuestionnaires(): Questionnaire[] {
  return getMockQuestionnairesForIntake();
}

function localTemplateQuestionnaires(): Questionnaire[] {
  return getAllLocalTemplates().map((t) => t.resource);
}

/**
 * Returns template options for the Rules Engine action editors.
 */
export function useTemplateLibraryOptions(): {
  loading: boolean;
  error: string | null;
  emailTemplateOptions: TemplateOption[];
  formTemplateOptions: TemplateOption[];
  allTemplateOptions: TemplateOption[];
  templateFilterSections: FilterMultiSelectSection[];
  getTemplateRef: (id: string) => TemplateLibraryRef | undefined;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const [liveOptions, setLiveOptions] = useState<TemplateOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [localVersion, setLocalVersion] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => subscribeToLocalTemplates(() => setLocalVersion((v) => v + 1)), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        let questionnaires: Questionnaire[] = [];
        try {
          questionnaires = await fetchTemplateQuestionnaires(medplum);
        } catch (fetchErr) {
          if (!isMockMedplumMode()) throw fetchErr;
          questionnaires = mockTemplateQuestionnaires();
        }

        if (questionnaires.length === 0 && isMockMedplumMode()) {
          questionnaires = mockTemplateQuestionnaires();
        }

        const localQs = localTemplateQuestionnaires();
        const merged = mergeTemplateOptions(
          loadTemplateLibraryOptions(questionnaires),
          loadTemplateLibraryOptions(localQs),
        );

        if (!cancelled) {
          setLiveOptions(merged);
          if (merged.length === 0 && !isMockMedplumMode()) {
            setError('No templates found in the Template Library.');
          }
        }
      } catch (err) {
        if (cancelled) return;
        const fallback = isMockMedplumMode()
          ? mergeTemplateOptions(
              loadTemplateLibraryOptions(mockTemplateQuestionnaires()),
              loadTemplateLibraryOptions(localTemplateQuestionnaires()),
            )
          : [];
        setLiveOptions(fallback);
        setError(err instanceof Error ? err.message : 'Failed to fetch templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [medplum, refreshKey, localVersion]);

  const allOptions = liveOptions;

  const emailTemplateOptions = useMemo(
    () => allOptions.filter((o) => o.type === 'email' || isEmailTemplate(o.category)),
    [allOptions],
  );

  const formTemplateOptions = useMemo(
    () => allOptions.filter(
      (o) => (o.type === 'form' || isAssignableFormTemplate(o.category)) && !isClientShareRestrictedTemplate(o.ref),
    ),
    [allOptions],
  );

  const templateFilterSections = useMemo(
    () => buildFormAssignmentTemplateFilterSections(allOptions),
    [allOptions],
  );

  const getTemplateRef = useMemo(() => {
    const map = new Map(allOptions.map((o) => [o.value, o.ref]));
    return (id: string): TemplateLibraryRef | undefined => map.get(id);
  }, [allOptions]);

  return {
    loading,
    error,
    emailTemplateOptions,
    formTemplateOptions,
    allTemplateOptions: allOptions,
    templateFilterSections,
    getTemplateRef,
    refresh,
  };
}
